import React from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';
import { LegalPageLayout, Section, Callout, Bullets } from '@/components/LegalPageLayout';

const UPDATED = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const SafetyPolicy = () => (
  <LegalPageLayout
    title="Safety Policy"
    lastUpdated={UPDATED}
    icon={LifeBuoy}
    testid="safety-page"
    intro={
      <p>
        Your safety as a user — both financial and account security — matters to us. This policy explains the real risks
        of options trading and how to use Option Wizard responsibly. It complements the{' '}
        <Link to="/terms" className="text-amber-300 hover:underline underline-offset-4">Terms of Service</Link> and the
        Important Legal Disclaimer shown when you enter the app.
      </p>
    }
  >
    <Callout tone="red" title="Educational tool — not financial advice">
      Option Wizard provides educational analysis and decision-support tools only. It does not provide
      financial, investment or trading advice, and it does not guarantee profitable outcomes. A disclaimer does not
      remove your responsibility to verify information and make your own decisions.
    </Callout>

    <Section title="1. Options Trading Involves Substantial Risk">
      <Bullets items={[
        'Options trading is not suitable for all investors and can result in the rapid and substantial loss of capital.',
        'You can lose more than your initial investment, particularly with certain short or multi-leg strategies.',
        'Leverage can amplify both gains and losses.',
        'Short options carry assignment risk, which can occur before expiration.',
        'Options can expire worthless, resulting in a total loss of the premium paid.',
      ]} />
    </Section>

    <Section title="2. Market, Volatility & Liquidity Risks">
      <Bullets items={[
        'Volatility can change quickly and materially affect option prices.',
        'Illiquid options may have wide bid/ask spreads and may be difficult to exit at a fair price.',
        'Market conditions can gap or move against a position faster than expected.',
      ]} />
    </Section>

    <Section title="3. Data & Calculation Limitations">
      <Bullets items={[
        'Market data may be delayed, inaccurate or incomplete, and is sourced from third parties.',
        'Analytics and payoff/P&L figures are estimates based on models and assumptions that may not match real conditions.',
        'Actual execution prices, fills, fees and assignment outcomes can differ from displayed estimates.',
        'Always independently verify important trade information with your broker and official sources before acting.',
      ]} />
    </Section>

    <Section title="4. AI Limitations">
      <p>
        AI-assisted features can be helpful for research and learning, but AI output may contain errors, omissions or
        inappropriate suggestions. Treat AI responses as one input among many — never the sole basis for a decision.
      </p>
    </Section>

    <Section title="5. Trade Responsibly">
      <Bullets items={[
        'Only trade with money you can afford to lose.',
        'Understand a strategy fully before using it.',
        'Consider consulting a qualified, licensed financial professional.',
        'Verify every important figure before placing a trade.',
      ]} />
    </Section>

    <Section title="6. Account Security">
      <Bullets items={[
        'Keep your login credentials confidential and use a strong, unique password.',
        'Only connect brokerage accounts you control, and disconnect them when no longer needed.',
        'Sign out on shared devices.',
      ]} />
    </Section>

    <Section title="7. Reporting Security Concerns">
      <p>
        If you discover a security vulnerability or notice suspicious activity on your account, please report it promptly
        through our{' '}
        <Link to="/contact" className="text-amber-300 hover:underline underline-offset-4">contact form</Link> so we can
        investigate. Please do not publicly disclose a suspected vulnerability before we have had a chance to respond.
      </p>
    </Section>
  </LegalPageLayout>
);

export default SafetyPolicy;
